import React from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './EnhancedSupabaseAuthContext';

/**
 * 🔒 PAGES RÉSERVÉES AUX UTILISATEURS CONNECTÉS
 * - Vérification initiale de la session : écran d'attente
 * - Visiteur non connecté : page de connexion, puis retour automatique à la page demandée
 * - requireVerification : adresse email confirmée exigée
 */
const EnhancedPrivateRoute = ({ children, requireVerification = false }) => {
  const { user, isInitialized } = useAuth();
  const location = useLocation();

  if (!isInitialized) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-gray-50" role="status" aria-live="polite">
        <div className="flex items-center gap-3 text-gray-600">
          <span className="h-6 w-6 animate-spin rounded-full border-2 border-blue-200 border-t-blue-700" aria-hidden="true" />
          <span className="font-medium">Vérification de votre session…</span>
        </div>
      </div>
    );
  }

  if (!user) {
    const from = location.pathname + location.search + location.hash;
    const message = from.startsWith('/submit')
      ? 'Connectez-vous (ou créez un compte gratuit) pour présenter votre projet.'
      : 'Connectez-vous pour accéder à cette page.';
    return <Navigate to="/login" replace state={{ from, message }} />;
  }

  if (requireVerification && !user.email_confirmed_at) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-sm">
          <h1 className="text-2xl font-bold text-gray-900">Confirmez votre adresse email</h1>
          <p className="mt-3 text-gray-600">Cliquez sur le lien reçu par email pour accéder à cette page.</p>
          <Link to="/confirm-email" className="mt-6 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-orange-500 px-6 py-3 font-semibold text-white hover:bg-orange-600">
            Je n'ai pas reçu l'email
          </Link>
        </div>
      </div>
    );
  }

  return children || <Outlet />;
};

export default EnhancedPrivateRoute;
